import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { FileText, Search, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { theme } from '../styles/theme';

const SummaryContainer = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  margin-top: ${theme.spacing[4]};
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.primary[100]}20);
  border-bottom: 1px solid ${theme.colors.border};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.md};
  color: white;
`;

const HeaderText = styled.div`
  h3 {
    margin: 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.lg};
  }
  
  p {
    margin: ${theme.spacing[1]} 0 0 0;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[100]};
    border-color: ${theme.colors.primary[400]};
    color: ${theme.colors.primary[600]};
  }
`;

const SearchContainer = styled.div`
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.backgroundSecondary};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]} ${theme.spacing[2]} ${theme.spacing[10]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  pointer-events: none;
`;

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SummaryContent = styled.div<{ $expanded: boolean; $searchActive: boolean }>`
  max-height: ${({ $expanded, $searchActive }) => {
    if ($searchActive) return 'none';
    return $expanded ? 'none' : '300px';
  }};
  overflow: auto;
  transition: max-height ${theme.transitions.base};
`;

const MarkdownContent = styled.div`
  padding: ${theme.spacing[5]};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.textPrimary};

  h1, h2, h3, h4, h5, h6 {
    color: ${theme.colors.textPrimary};
    margin: ${theme.spacing[4]} 0 ${theme.spacing[2]} 0;
    font-weight: ${theme.typography.fontWeight.semibold};
    
    &:first-child {
      margin-top: 0;
    }
  }

  h1 { font-size: ${theme.typography.fontSize.xl}; }
  h2 { font-size: ${theme.typography.fontSize.lg}; }
  h3 { font-size: ${theme.typography.fontSize.base}; }

  p {
    margin: 0 0 ${theme.spacing[3]} 0;
    color: ${theme.colors.textSecondary};
  }

  ul, ol {
    margin: 0 0 ${theme.spacing[3]} 0;
    padding-left: ${theme.spacing[5]};
    
    li {
      margin-bottom: ${theme.spacing[1]};
      color: ${theme.colors.textSecondary};
    }
  }

  blockquote {
    margin: ${theme.spacing[3]} 0;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    background: ${theme.colors.primary[50]};
    border-left: 4px solid ${theme.colors.primary[500]};
    border-radius: 0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0;
    
    p {
      margin: 0;
      color: ${theme.colors.textPrimary};
      font-style: italic;
    }
  }

  code {
    background: ${theme.colors.backgroundSecondary};
    padding: ${theme.spacing[1]} ${theme.spacing[2]};
    border-radius: ${theme.borderRadius.sm};
    font-family: ${theme.typography.fontFamily.mono};
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textPrimary};
  }

  strong {
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
  }

  em {
    font-style: italic;
    color: ${theme.colors.textPrimary};
  }

  mark {
    background: ${theme.colors.primary[200]};
    color: ${theme.colors.textPrimary};
    padding: 0 ${theme.spacing[1]};
    border-radius: ${theme.borderRadius.sm};
  }
`;

const ExpandToggle = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]};
  background: ${theme.colors.backgroundSecondary};
  border: none;
  border-top: 1px solid ${theme.colors.border};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[8]};
  text-align: center;
  color: ${theme.colors.textMuted};
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[3]};
  color: ${theme.colors.primary[500]};
`;

interface RequirementsSummaryProps {
  summary?: string;
}

const RequirementsSummary: React.FC<RequirementsSummaryProps> = ({ summary }) => {
  const [expanded, setExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [copied, setCopied] = useState(false);

  const filteredContent = useMemo(() => {
    if (!summary || !searchTerm.trim()) return summary;
    
    // Simple search highlight using HTML tags instead of markdown
    const regex = new RegExp(`(${searchTerm.trim()})`, 'gi');
    return summary.replace(regex, '<mark>$1</mark>');
  }, [summary, searchTerm]);

  const handleCopy = async () => {
    if (summary) {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const renderMarkdown = (text: string) => {
    // Enhanced markdown renderer to handle more formatting
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let inCodeBlock = false;
    let codeBlockContent: string[] = [];
    let inTable = false;
    let tableRows: string[] = [];

    // Hoisted out of the forEach loop so heading and list-item handlers can
    // apply inline formatting too (they previously pushed raw text, causing
    // **bold** and `code` markers to bleed through in bullets and headings).
    const processInlineFormatting = (value: string) => {
      let out = value.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      out = out.replace(/`([^`]+?)`/g, '<code>$1</code>');
      out = out.replace(/\*([^*<>]+?)\*/g, '<em>$1</em>');
      return out;
    };

    lines.forEach((line, index) => {
      // Handle code blocks
      if (line.trim().startsWith('```')) {
        if (inCodeBlock) {
          // End code block
          elements.push(
            <pre key={`code-${index}`} style={{ 
              background: theme.colors.backgroundSecondary, 
              padding: theme.spacing[3], 
              borderRadius: theme.borderRadius.md,
              overflow: 'auto',
              fontSize: theme.typography.fontSize.xs,
              fontFamily: theme.typography.fontFamily.mono
            }}>
              <code>{codeBlockContent.join('\n')}</code>
            </pre>
          );
          codeBlockContent = [];
          inCodeBlock = false;
        } else {
          // Start code block
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeBlockContent.push(line);
        return;
      }

      // Handle tables
      if (line.includes('|') && line.split('|').length > 2) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        tableRows.push(line);
        return;
      } else if (inTable) {
        // End table
        elements.push(
          <div key={`table-${index}`} style={{ 
            overflow: 'auto', 
            margin: `${theme.spacing[3]} 0`,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.md
          }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: theme.typography.fontSize.xs
            }}>
              <tbody>
                {tableRows.map((row, rowIndex) => (
                  <tr key={rowIndex} style={{ 
                    borderBottom: `1px solid ${theme.colors.border}` 
                  }}>
                    {row.split('|').filter(cell => cell.trim()).map((cell, cellIndex) => (
                      <td key={cellIndex} style={{ 
                        padding: theme.spacing[2], 
                        borderRight: cellIndex < row.split('|').length - 2 ? `1px solid ${theme.colors.border}` : 'none',
                        backgroundColor: rowIndex === 0 ? theme.colors.backgroundSecondary : 'transparent'
                      }}>
                        {cell.trim()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
        inTable = false;
      }

      // Handle horizontal rules
      if (line.trim() === '---' || line.trim() === '***') {
        elements.push(
          <hr key={index} style={{ 
            border: 'none', 
            borderTop: `1px solid ${theme.colors.border}`, 
            margin: `${theme.spacing[4]} 0` 
          }} />
        );
        return;
      }

      // Handle headings
      if (line.startsWith('# ')) {
        elements.push(<h1 key={index} dangerouslySetInnerHTML={{ __html: processInlineFormatting(line.slice(2)) }} />);
        return;
      }
      if (line.startsWith('## ')) {
        elements.push(<h2 key={index} dangerouslySetInnerHTML={{ __html: processInlineFormatting(line.slice(3)) }} />);
        return;
      }
      if (line.startsWith('### ')) {
        elements.push(<h3 key={index} dangerouslySetInnerHTML={{ __html: processInlineFormatting(line.slice(4)) }} />);
        return;
      }
      if (line.startsWith('#### ')) {
        elements.push(
          <h4 key={index}
              style={{ fontSize: theme.typography.fontSize.sm }}
              dangerouslySetInnerHTML={{ __html: processInlineFormatting(line.slice(5)) }} />
        );
        return;
      }

      // Handle lists
      if (line.startsWith('- ') || line.startsWith('* ')) {
        elements.push(
          <li key={index}
              style={{ listStyle: 'disc', marginLeft: theme.spacing[4] }}
              dangerouslySetInnerHTML={{ __html: processInlineFormatting(line.slice(2)) }} />
        );
        return;
      }

      // Handle empty lines
      if (line.trim() === '') {
        elements.push(<br key={index} />);
        return;
      }

      // Handle regular paragraphs with inline formatting
      elements.push(
        <p key={index} dangerouslySetInnerHTML={{ __html: processInlineFormatting(line) }} />
      );
    });

    return elements;
  };

  if (!summary) {
    return (
      <SummaryContainer>
        <EmptyState>
          <EmptyIcon>
            <FileText size={24} />
          </EmptyIcon>
          <p>Requirements summary will appear here once processing is complete...</p>
        </EmptyState>
      </SummaryContainer>
    );
  }

  return (
    <SummaryContainer>
      <SummaryHeader>
        <HeaderContent>
          <HeaderIcon>
            <FileText size={20} />
          </HeaderIcon>
          <HeaderText>
            <h3>Requirements Summary</h3>
            <p>Extracted and processed from tender document</p>
          </HeaderText>
        </HeaderContent>
        
        <HeaderActions>
          <ActionButton onClick={() => setShowSearch(!showSearch)} title="Search">
            <Search size={16} />
          </ActionButton>
          <ActionButton onClick={handleCopy} title="Copy to clipboard">
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </ActionButton>
        </HeaderActions>
      </SummaryHeader>

      {showSearch && (
        <SearchContainer>
          <SearchWrapper>
            <SearchIcon>
              <Search size={16} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search in requirements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchWrapper>
        </SearchContainer>
      )}

      <SummaryContent $expanded={expanded} $searchActive={!!searchTerm.trim()}>
        <MarkdownContent>
          {renderMarkdown(filteredContent || summary)}
        </MarkdownContent>
      </SummaryContent>

      {!searchTerm.trim() && (
        <ExpandToggle onClick={() => setExpanded(!expanded)}>
          {expanded ? (
            <>
              <ChevronUp size={16} />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown size={16} />
              Show More
            </>
          )}
        </ExpandToggle>
      )}
    </SummaryContainer>
  );
};

export default RequirementsSummary;